import { useMemo, useRef, useState } from "react";
import { isDividerCode } from "../utils/taxonomyMap";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useAnchoredMenu } from "../hooks/useAnchoredMenu"; // 134 §1
import MenuSelect from "./MenuSelect"; // 154B §1 — the select face 151F built
import type { MenuSelectOption } from "./MenuSelect";
import type { TaxList, TFn } from "../lib/taxonomyTypes";
import { taxonomyValueState } from "../lib/taxonomyHelpers";
import { Z } from "../lib/tokens";
import { NO_AUTOFILL } from "../lib/noAutofill"; // 213B §8 — no sign-ins, no contacts, in a picker
import { useMenuLevel } from "../lib/paneStacking"; // 204 §1

// 05m-b commit 1 — ONE select where fourteen were open-coded.
//
// PERMANENT INFRASTRUCTURE, NOT A STOPGAP FOR 05n. The obvious objection is
// that 05m/05n convert these columns to codes and reseed the forms from
// resolved codes, so this is temporary. It is not: a select whose stored value
// is absent from its option list is a shape that survives the conversion (an
// entry archived or re-keyed after a row was saved does it), and brief 23 §3's
// ruling — leave the residue alone and report it for a human to re-pick — is
// UNIMPLEMENTABLE without it, because every one of those rows opens in a form
// that shows what it holds as an em-dash. Nobody removes this after 05n.
//
// WHY ONE, per THE FOUR RULES #3 (extract, don't improve): thirteen sites is
// twelve chances to forget one, and the census found a fourteenth
// (CollectiveCaseView's inline case modal) that the brief's App-only list did
// not have. This commit MOVES them; commit 2 adds the provisional option in
// this one place.
//
// THE UNION, per THE FOUR RULES #2 — every prop below is one some call site
// actually passes, not one that seemed reasonable:
//   label      all fourteen (`_fl("Country")`, `t("field.type_required")`, …)
//   value      all — but from three different stores (App's `form`, App's
//              `quickAddForm`, CCV's `f`), which is why it is a value and a
//              callback rather than a form object and a key
//   options    two shapes, both live: bare label strings (`countriesList`,
//              `titlesList`, `barsList`, `stageTypesList`) and `[value, label]`
//              tuples (everything reached through App's `S()`), including the
//              `["", "—"]` empty option three sites already prepend that way
//   fallback   the seven country sites' `form.country || "Belgium"` — a
//              DISPLAY default that never reaches form state (see below).
//              207 §13 — MEASURED: no country site passes it any more (05n
//              #246 took the six saves' « Belgium » to `|| null`, and the
//              country selects dropped the prop); its callers today are the
//              collaborator's role, the client's language and the case's
//              status. A country field shows what is stored.
//   disabled   the bar select's `disabled={!_coAdm}`
//
// `fallback` IS SEPARATE FROM `value` ON PURPOSE, and commit 2 is the reason.
// `form.country || "Belgium"` renders "Belgium" for a row whose country is
// EMPTY, so folding it into `value` would let commit 2 mark an empty field as
// holding a value it does not hold. The primitive is told what the record says
// and, separately, what to show when it says nothing.

// ── commit 2 — THE PROVISIONAL OPTION ────────────────────────────────────────
//
// THE INCIDENT: 2026-08-03, a title cleared by an administrator acting
// correctly on a form that showed a filled field as empty. `Modifier le
// collaborateur` opened on a row storing `title = "Mrs."`; the French option
// list holds `M. · Mme · Mlle · Me. · Dr. · Prof.`, none of which is "Mrs.", so
// the control fell to its empty option and displayed `—`. Read as "this person
// has no title" — the only reasonable reading — and cleared. The staging row
// still shows what that costs: Collaborator 16, `title = ''`, written
// 2026-08-03 06:41:29Z.
//
// So `—` meant two things and nothing told them apart: *this field is empty*,
// and *this field holds something I cannot match*. The form displayed a filled
// field as empty, and the natural repair — picking a value — was the only
// action that destroyed the original.
//
// THE FIX: when the stored value matches no option, prepend an option carrying
// that exact value, marked, and let it be the one displayed.
//   · VALUE = the stored string, so opening a form and closing it changes
//     nothing at all, and saving after editing another field writes back what
//     was already there.
//   · TEXT = the stored string plus a mark, so the user reads what the record
//     actually holds instead of an em-dash that hides it.
//   · Picking a real option replaces it and it disappears, because the value
//     now matches.
//
// This is slice 20's ruling, applied to a select. `provisionalLevel`
// (utils.ts:631) shows an unresolvable ladder code muted rather than an
// em-dash, on the reasoning that the only surviving evidence of what the record
// said is exactly what someone re-picking needs to read; `TaxonomyOrphanMark`
// (05m step C §5) renders the same two states beside a value. Same argument,
// same shape, third surface.
//
// AMBIGUITY IS A DIFFERENT STATE AND IT IS THE HARDER ONE. `"Mr."` is `mr`'s
// English label AND `me`'s Dutch one — and `me` is Maitre/Meester, a different
// person entirely. That value is not missing from the vocabulary; it is IN it
// twice. The select cannot see this on its own, because in each language only
// ONE option carries the string: in English `mr`'s, in Dutch `me`'s. The
// control therefore looks perfectly healthy in both, and silently asserts a
// different entry in each. (Measured, not assumed: across all six kinds on
// staging exactly one string is claimed by two entries, and it is this one.)
//
// So ambiguity gets the provisional option too, EVEN THOUGH an option matches,
// because the bar is that a user must not be able to look at this control and
// believe the app knows which entry the row means when it does not. That needs
// the vocabulary, not the option list, which is why `entries` is a prop.
//
// A consequence, stated because it is real and not a bug: while these columns
// store LABELS, re-picking the colliding option does not disambiguate anything
// — it stores the same string, and the mark correctly stays. The pick that
// resolves it is a DIFFERENT label (`Dhr.`, `M.`), or 05n's conversion to
// codes. The control says so rather than pretending otherwise.

export type FieldSelectOption = string | readonly string[];

type Props = {
  label?: ReactNode;
  // What the record holds. Nullable because every one of these columns is.
  value?: string | null;
  onChange: (v: string) => void;
  options?: readonly FieldSelectOption[] | null;
  // Shown when `value` is empty (207 §13: no country site passes it — see the
  // header's `fallback` row; the six person modals' old save mirror,
  // `country: form.country || "Belgium"`, is `|| null` since 05n). It is NOT what the
  // record holds, so the provisional option is never computed from it — a
  // fallback marked as stored data would be the same lie in the other
  // direction.
  fallback?: string;
  disabled?: boolean;
  // The kind's TaxonomyEntry rows, where this select renders one. Present, the
  // ambiguous state above is detectable; absent, an unmatched value is still
  // marked, because "no option carries this" needs no vocabulary.
  entries?: TaxList;
  t?: TFn;
  // 05n §2 — the combobox, for a list nobody can scroll. See the block below.
  searchable?: boolean;
  placeholder?: string;
};

// ── 05n §2 — SEARCHABLE, and why a native <select> stopped being enough ──────
//
// `country` became the full ISO 3166-1 list: 249 entries where there were
// seven. Brief 23 §2 ruled that with the vocabulary — "The dropdown will need
// typeahead at ~250 entries; that is part of the work, not a follow-up" —
// because a closed universal vocabulary is only usable if you can find your
// country in it.
//
// A native <select> is not simply worse here, it is wrong in a specific way.
// Its built-in type-ahead matches a PREFIX of the option text, so a user
// looking for the United Kingdom must know to type "Uni", and one looking for
// "Congo - Kinshasa" must know which of CLDR's two Congos comes first. And the
// entries arrive in the seed's order, which is alphabetical by ENGLISH — so a
// French user reading "Allemagne" finds it filed under G.
//
// Both are fixed here rather than in the data, because both are about the
// READER: `sortOptions` orders by the label this user is being shown, and
// `filterOptions` matches a SUBSTRING, accent- and case-insensitively, so
// "koninkrijk", "kingdom" and "royaume" each find GB in their own language.
//
// THE PROVISIONAL OPTION SURVIVES IT. A searchable field that dropped the mark
// would re-open the hole 05m-b closed — worse, because a text input showing a
// resolved label looks even more authoritative than a select. The mark is
// rendered as the first row of the menu and as the input's own text, exactly as
// the <option> version is.
//
// Exported because the recording drives them directly: a filter and a sort are
// pure functions over an option list, and proving them needs no DOM. What the
// recording cannot prove is the RENDER, and that is stated in the PR.

/** Case- and accent-insensitive fold, for matching only — never for storing. */
export const foldForSearch = (s: string) =>
  String(s ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/** Substring match over the LABEL. An empty query matches everything. */
export function filterOptions(
  opts: ReadonlyArray<readonly [string, string]>,
  query: string,
) {
  const q = foldForSearch(query);
  if (!q) return opts.slice();
  return opts.filter(([, l]) => foldForSearch(l).includes(q));
}

/** Alphabetical by the label the reader sees. A leading empty-value option is
 *  a placeholder ("— None —"), not an entry, so it is pinned first. */
export function sortOptions(opts: ReadonlyArray<readonly [string, string]>) {
  const blanks = opts.filter(([v]) => v === "");
  const rest = opts.filter(([v]) => v !== "");
  rest.sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: "base" }));
  return [...blanks, ...rest];
}

// The mark's text. Two states, distinct wording, and the long hint rides along
// as the option's tooltip — the same two keys TaxonomyOrphanMark uses, so the
// wording of a re-pick instruction lives in one place.
function provisionalOption(
  value: string,
  ambiguous: boolean,
  t?: TFn,
): { text: string; hint: string } {
  const _t: TFn = t || ((_k, o) => (o && o.fallback) || "");
  return ambiguous
    ? {
        text: _t("taxonomy.option.ambiguous", {
          value,
          fallback: `${value} — matches more than one entry`,
        }),
        hint: _t("taxonomy.ambiguous.hint", {
          fallback:
            "This value matches more than one entry in the list, so it cannot be shown in another language. Pick the one you mean.",
        }),
      }
    : {
        text: _t("taxonomy.option.unlisted", {
          value,
          fallback: `${value} — not in the list`,
        }),
        hint: _t("taxonomy.orphan.hint", {
          fallback:
            "This entry no longer exists in the list — the value shown is what the record still holds. Pick a current one to relink it.",
        }),
      };
}

// A bare string is its own value and its own label; a tuple carries both.
// Exported because the recording needs to compute what the PRE-slice select
// displayed from the same list, and a second copy of this two-line rule in the
// harness is a second thing to get wrong — it already was, once.
export const pair = (o: FieldSelectOption): [string, string] =>
  typeof o === "string"
    ? [o, o]
    : [String(o[0] ?? ""), String(o[1] ?? o[0] ?? "")];

export default function FieldSelect({
  label,
  value,
  onChange,
  options,
  fallback,
  disabled,
  entries,
  t,
  searchable,
  placeholder,
}: Props) {
  const stored = value ?? "";
  const shown = stored || fallback || "";
  const opts = (options || []).map(pair);
  // Computed from `stored`, never from `shown`: an empty field showing a
  // fallback holds nothing, and marking it would say otherwise.
  const carried = opts.some(([v]) => v === stored);
  const ambiguous =
    !!entries && taxonomyValueState(stored, entries) === "ambiguous";
  const provisional = stored !== "" && (!carried || ambiguous);
  const mark = provisional
    ? provisionalOption(stored, ambiguous, t)
    : null;
  if (searchable) {
    return (
      <FieldCombo
        label={label}
        stored={stored}
        shown={shown}
        opts={opts}
        mark={mark}
        disabled={disabled}
        placeholder={placeholder}
        onChange={onChange}
      />
    );
  }
  /* ── BRIEF 154B §1 — THE MENU IS THE APP'S, AND IT IS THE ONE THAT EXISTS ──
   *
   * The native <select> that stood here painted its open list with the OS: black
   * under macOS dark mode, unstyleable, and the reason B.3 exists. What replaces
   * it is NOT a new popover. 151F built `MenuSelect` — "the select face over the
   * SAME popover" — over useAnchoredMenu's placement, TypeaheadPicker's imported
   * dress, the modals' ScrollHint discs and the 108 §6b divider row, and its own
   * header says it "is what that sweep would mount". This is that mount. Sixteen
   * call sites change nothing; the three `searchable` ones keep FieldCombo.
   *
   * THE PROVISIONAL OPTION SURVIVES, which was the condition. It is still FIRST
   * and it still carries the stored value and the hint — the hint is why
   * MenuSelect learned a third tuple slot, because a marked row that cannot say
   * "pick a current one to relink it" has lost the sentence that tells a reader
   * what to do. And the closed face now shows the mark's TEXT, because a value
   * matching no option matches this one.
   *
   * ONE BEHAVIOUR DELIBERATELY DOES NOT SURVIVE, and it is the incident. When a
   * value matched nothing, react-dom marked the FIRST option selected
   * (react-dom 18.3.1, cjs/react-dom.development.js:2131-2155) — which is how an
   * unmatched title displayed `—`, and how an unmatched country "came to display
   * a different, real, wrong country" (this file's own header, 2026-08-03). A
   * button face cannot reproduce that without implementing the lie on purpose:
   * it shows the value it holds, or nothing. The prepended mark covers every
   * case where the value is non-empty, so what is actually lost is a wrong
   * answer for an empty value — and it is lost in the direction 05m-b was
   * written to move.
   */
  const menuOptions: MenuSelectOption[] = mark
    ? [[stored, mark.text, mark.hint], ...opts]
    : opts;
  return (
    <div className="field">
      <label>{label}</label>
      <MenuSelect
        value={shown}
        onChange={onChange}
        options={menuOptions}
        disabled={disabled}
        className="field-input"
        /* a <select> announces its own value; a button announces its text and
           nothing else, so the field's label — where it is a plain string —
           becomes the control's accessible name. The visible <label> above is
           unchanged and still unassociated, exactly as it was. */
        ariaLabel={typeof label === "string" ? label : undefined}
      />
    </div>
  );
}

// ── the combobox ─────────────────────────────────────────────────────────────
//
// A separate component and not a branch inside FieldSelect, because it holds
// hooks: a wrapper containing useState inside another component's body is the
// Bug-A shape, and the rules of hooks make the function-call shortcut illegal
// (CLAUDE.md, "Known patterns"). It sits at module scope with its state its own.
//
// PORTALLED, per the repo's documented pattern for a menu inside an
// overflow-clipped container (CLAUDE.md, "Portal pattern for dropdowns"): the
// six person modals scroll, `.ML` clips, and a menu 249 rows long inside a
// clipped box is a menu nobody can reach. The trigger's rect is captured on
// open; the menu and its dismiss backdrop render into document.body at
// `position: fixed`. Close on backdrop, pick, Escape or blur — NOT on scroll,
// which that same note explains is a trap (a capture-phase scroll listener
// catches programmatic scroll restores and shuts the menu mid-use).
function FieldCombo({
  label,
  stored,
  shown,
  opts,
  mark,
  disabled,
  placeholder,
  onChange,
}: {
  label?: ReactNode;
  stored: string;
  shown: string;
  opts: Array<[string, string]>;
  mark: { text: string; hint: string } | null;
  disabled?: boolean;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  /* 204 §1 — the stylesheet's `.fs-combo-backdrop` / `.fs-combo-menu` carry
     the popover pair (200 / 201); inside a ModalLayout LIFTED over an open
     pane the two take one over the lifted shell inline, else nothing is set
     and the class rules stand. */
  const menuZ = useMenuLevel(Z.popover);
  const lifted = menuZ !== Z.popover;

  // What the closed field reads. The mark wins, for the same reason it is the
  // first <option> above: a row holding something the list cannot match must
  // show what it holds, not the nearest label.
  const sorted = useMemo(() => sortOptions(opts), [opts]);
  const current = sorted.find(([v]) => v === shown);
  const closedText = mark ? mark.text : current ? current[1] : "";
  const visible = useMemo(() => filterOptions(sorted, query), [sorted, query]);

  /* 134 §1 — the resize dismissal and the measured placement that stood here
     are `useAnchoredMenu` now, unchanged and with their reasoning. `deps` is
     this control's own re-place trigger: the filtered list's length and the
     provisional mark, exactly the two the effect carried before the move. */
  useAnchoredMenu({
    open,
    anchorRef: boxRef,
    menuRef,
    onDismiss: () => setOpen(false),
    deps: [visible.length, mark],
  });

  const openMenu = () => {
    if (disabled) return;
    setQuery("");
    setActive(0);
    setOpen(true);
  };
  const pick = (v: string) => {
    if (isDividerCode(v)) return; // 108 §6b — a divider is never a value
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="field">
      <label>{label}</label>
      {/* 05r — IT DID NOT LOOK LIKE A PICKER. Laurent's glance: "no chevron,
          reads as a text input." It is a combobox, so it must accept typing and
          cannot be a bare <select>; what it was missing is the one mark every
          other control in this app uses to say a list is behind it. The caret is
          decorative and `pointer-events: none`, so clicking it lands on the
          input underneath and opens the menu exactly as clicking the text does. */}
      <div className="fs-combo-wrap">
      <input
        ref={boxRef}
        /* 213B §8 — not a sign-in field (src/lib/noAutofill.ts); its own
           autoComplete="off" is now the bundle's, with 207 §18's class */
        {...NO_AUTOFILL}
        className="field-input fs-combo-input no-contact-autofill"
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        disabled={disabled}
        title={mark ? mark.hint : undefined}
        placeholder={placeholder}
        value={open ? query : closedText}
        onFocus={openMenu}
        onClick={openMenu}
        onChange={(e) => {
          if (!open) openMenu();
          setQuery(e.target.value);
          setActive(0);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") { setOpen(false); return; }
          if (!open) { if (e.key === "ArrowDown") openMenu(); return; }
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, visible.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
          else if (e.key === "Enter") {
            e.preventDefault();
            const hit = visible[active];
            if (hit) pick(hit[0]);
          }
        }}
      />
      <span className="fs-combo-caret" aria-hidden="true" />
      </div>
      {open &&
        createPortal(
          <>
            <div
              className="fs-combo-backdrop"
              style={lifted ? { zIndex: menuZ - 1 } : undefined}
              onMouseDown={() => setOpen(false)}
            />
            <div
              ref={menuRef}
              className="fs-combo-menu"
              /* Hidden until the layout effect has measured and placed it. A
                 menu that paints at the origin for one frame is the bug this
                 slice fixes, in miniature. */
              style={lifted ? { visibility: "hidden", top: 0, left: 0, zIndex: menuZ } : { visibility: "hidden", top: 0, left: 0 }}
            >
              {/* The mark, first, so a row whose value the list cannot match
                  says so before it offers alternatives. Picking it is a no-op
                  that closes the menu — it is what the record already holds. */}
              {mark && (
                <div
                  className="fs-combo-opt fs-combo-mark"
                  title={mark.hint}
                  onMouseDown={() => pick(stored)}
                >
                  {mark.text}
                </div>
              )}
              {visible.length === 0 && (
                <div className="fs-combo-empty">—</div>
              )}
              {visible.map(([v, l], i) =>
                /* 108 §6b — the custom picker draws a rule element; the row
                   takes no hover-active and pick() refuses its code, so the
                   keyboard march may pass over it but can never commit it. */
                isDividerCode(v) ? (
                  <div
                    key={v}
                    aria-hidden="true"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "2px 8px",
                      color: "var(--tm)",
                      fontSize: 10,
                      pointerEvents: "none",
                    }}
                  >
                    <span style={{ flex: 1, borderTop: "1px solid var(--br)" }} />
                    {l ? <span>{l}</span> : null}
                    <span style={{ flex: 1, borderTop: "1px solid var(--br)" }} />
                  </div>
                ) : (
                  <div
                    key={v}
                    className={"fs-combo-opt" + (i === active ? " is-active" : "")}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={() => pick(v)}
                  >
                    {l}
                  </div>
                ),
              )}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
